import type { JSX } from "react";

import magnisLogoRoundedUrl from "../../../../desktop/icon-rounded.png";
import type { SeedingPhase, SeedingProgress } from "./helpers";

/**
 * Purely presentational. Whether it appears at all is `DemoGate`'s decision —
 * having the splash decide its own visibility from a profile was how the same
 * question ended up answered in two places.
 */
interface SeedingSplashProps {
  readonly failed: boolean;
  readonly progress: SeedingProgress | null;
  readonly actionError: string | null;
  readonly actionPending: "retry" | "refresh" | null;
  readonly onRetry: () => void;
}

const PHASE_LABELS: Readonly<Record<SeedingPhase, string>> = {
  resolving_dataset: "Preparing workspace data…",
  materializing: "Building your workspace…",
  complete: "Opening your workspace…",
};

export function SeedingSplash({
  failed,
  progress,
  actionError,
  actionPending,
  onRetry,
}: SeedingSplashProps): JSX.Element {
  const phaseLabel =
    progress === null ? "Starting workspace setup…" : PHASE_LABELS[progress.phase];
  const progressPercent =
    progress?.totalItems == null
      ? null
      : progress.totalItems === 0
        ? 100
        : Math.round((progress.completedItems / progress.totalItems) * 100);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-seeding-title"
      data-testid="onboarding-seeding-splash"
      className="fixed inset-0 z-50 flex items-center justify-center bg-surface px-5 py-8 text-content"
    >
      <div className="flex w-full max-w-[420px] flex-col items-center text-center">
        <img
          src={magnisLogoRoundedUrl}
          alt="Magnis"
          className="h-12 w-12 rounded-xl"
        />
        <h1
          id="onboarding-seeding-title"
          className="mt-5 text-xl font-medium tracking-tight"
        >
          {failed
            ? "We couldn't prepare your workspace"
            : "Setting up your workspace"}
        </h1>

        {!failed ? (
          <div className="mt-6 w-full">
            <div
              role="progressbar"
              aria-label="Workspace setup progress"
              aria-valuetext={phaseLabel}
              {...(progress?.totalItems == null
                ? {}
                : {
                    "aria-valuemin": 0,
                    "aria-valuemax": progress.totalItems,
                    "aria-valuenow": progress.completedItems,
                  })}
              className="h-[3px] overflow-hidden rounded-full bg-content-tertiary/20"
            >
              <div
                className={`h-full rounded-full bg-accent transition-[width] duration-300 ${
                  progressPercent === null ? "w-1/3 animate-pulse" : ""
                }`}
                style={
                  progressPercent === null
                    ? undefined
                    : { width: `${String(progressPercent)}%` }
                }
              />
            </div>
            <p className="mt-2 text-xs leading-5 text-content-secondary">
              {phaseLabel}
            </p>
          </div>
        ) : null}

        {actionError !== null ? (
          <p role="alert" className="mt-5 text-sm text-status-error">
            {actionError}
          </p>
        ) : null}
        {failed || actionError !== null ? (
          <button
            type="button"
            onClick={onRetry}
            disabled={actionPending !== null}
            className="mt-5 rounded-xl bg-accent px-4 py-3 text-sm font-semibold text-on-bright disabled:cursor-not-allowed disabled:opacity-50"
          >
            {actionPending !== null ? "Trying again…" : "Try again"}
          </button>
        ) : null}
      </div>
    </div>
  );
}
